import { reactive, computed, WritableComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

type Locales = 'ja' | 'en' | 'es' | 'fr' | 'pt' | 'de' | 'it'

const regions = ['Asia', 'Europe', 'America'] as const
type RegionType = typeof regions[number]

type MaintenanceStatusType = {
  contents: {
    [key in Locales]: {
      title: string
      message: string
    } | null
  }
  availableStatus: {
    [key in RegionType]: boolean
  }
}

/**
 * メンテナンスファイルを S3 から取得し、その内容を取得するための Hook
 */
export default function useMaintenanceStatus() {
  const state = reactive({
    maintenanceStatus: null as MaintenanceStatusType | null,
  })
  const locale = useI18n().locale as WritableComputedRef<Locales>

  const fetchMaintenanceStatus = async () => {
    state.maintenanceStatus = await axios
      .get(process.env.VUE_APP_MAINTENANCE_FILE_PATH)
      .then(res => JSON.parse(JSON.stringify(res.data)))
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
  }

  const title = computed(() => {
    return (
      state.maintenanceStatus?.contents[locale.value]?.title ||
      state.maintenanceStatus?.contents.en?.title
    )
  })

  const message = computed(() => {
    return (
      state.maintenanceStatus?.contents[locale.value]?.message ||
      state.maintenanceStatus?.contents.en?.message
    )
  })

  const availableStatus = computed(() => {
    return (
      state.maintenanceStatus?.availableStatus || {
        Asia: true,
        Europe: true,
        America: true,
      }
    )
  })

  /**
   * env ファイルの API BASE URLを元にサーバがメンテナンス中かどうかを判定する
   * API_BASE_URL 以外のサーバの API を利用しないページ（トークン付きのURLで遷移してくるページなど）で利用する。
   */
  const checkMaintenanceStatusByApiBaseUrl = async () => {
    await fetchMaintenanceStatus()
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL
    const apiBaseUrlMap = JSON.parse(process.env.VUE_APP_ALL_API_BASE_URL || {}) as {
      [key: string]: string
    }
    const server = Object.keys(apiBaseUrlMap).find(key => apiBaseUrl === apiBaseUrlMap[key])
    const isRegion = (str: string | undefined): str is RegionType => {
      return regions.some(value => value === str)
    }
    return isRegion(server) ? availableStatus.value[server] : true
  }

  return {
    fetchMaintenanceStatus,
    title,
    message,
    availableStatus,
    checkMaintenanceStatusByApiBaseUrl,
  }
}
